"use strict";
exports.__esModule = true;
exports.TransactionService = void 0;
var tslib_1 = require("tslib");
var bignumber_js_1 = tslib_1.__importDefault(require("bignumber.js"));
var ethers_1 = require("ethers");
var utils_1 = require("ethers/lib/utils");
var config_1 = require("../common/config");
var chain_provider_1 = require("../factory/chain-provider");
var gas_price_multipliers_1 = require("./constants/gas-price-multipliers");
var transaction_status_1 = require("./constants/transaction-status");
var transaction_speed_1 = tslib_1.__importDefault(require("./types/transaction-speed"));
var providers_1 = require("@ethersproject/providers");
var utils_2 = require("../common/utils");
var gelato_1 = require("../gasless-providers/gelato");
var TransactionService = /** @class */ (function () {
    function TransactionService() {
    }
    TransactionService.prepareTransaction = function (txRequest, chainID, speed) {
        if (speed === void 0) { speed = transaction_speed_1["default"].Average; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var chainProvider, fees, _a;
            var _b;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0: return [4 /*yield*/, (0, chain_provider_1.getChainProvider)(chainID)];
                    case 1:
                        chainProvider = _c.sent();
                        return [4 /*yield*/, this.getFees(speed, chainProvider)];
                    case 2:
                        fees = _c.sent();
                        _a = [tslib_1.__assign(tslib_1.__assign({}, txRequest), fees)];
                        _b = {};
                        return [4 /*yield*/, this.estimateGas(txRequest, chainProvider)];
                    case 3:
                        _b.gasLimit = _c.sent();
                        return [4 /*yield*/, chainProvider.getTransactionCount(txRequest.from)];
                    case 4: return [2 /*return*/, tslib_1.__assign.apply(void 0, [tslib_1.__assign.apply(void 0, _a.concat([(_b.nonce = _c.sent(), _b)])), (!!fees.maxFeePerGas && { type: 2 })])];
                }
            });
        });
    };
    TransactionService.sendGaslessly = function (contractAddress, data, pkOrProvider, chainID) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var wallet, sender;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(typeof pkOrProvider !== "string")) return [3 /*break*/, 3];
                        wallet = new providers_1.Web3Provider(pkOrProvider).getSigner();
                        return [4 /*yield*/, wallet.getChainId()];
                    case 1:
                        chainID = _a.sent();
                        return [4 /*yield*/, wallet.getAddress()];
                    case 2:
                        sender = _a.sent();
                        return [3 /*break*/, 6];
                    case 3: return [4 /*yield*/, (0, utils_2.getWallet)(pkOrProvider, chainID)];
                    case 4:
                        wallet = _a.sent();
                        return [4 /*yield*/, wallet.getAddress()];
                    case 5:
                        sender = _a.sent();
                        _a.label = 6;
                    case 6: return [2 /*return*/, gelato_1.Gelato.sendTx(chainID, contractAddress, sender, data, pkOrProvider)];
                }
            });
        });
    };
    TransactionService.getTransactionStatus = function (hash, chainID, minBlockConfirmation) {
        if (minBlockConfirmation === void 0) { minBlockConfirmation = 0; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var chainProvider, tx, status_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, (0, chain_provider_1.getChainProvider)(chainID)];
                    case 1:
                        chainProvider = _a.sent();
                        return [4 /*yield*/, chainProvider.getTransaction(hash)];
                    case 2:
                        tx = _a.sent();
                        if (!tx)
                            throw Error(transaction_status_1.TX_STATUS.NOT_FOUND);
                        if (!(tx.confirmations &&
                            tx.confirmations > minBlockConfirmation)) return [3 /*break*/, 4];
                        return [4 /*yield*/, chainProvider.getTransactionReceipt(hash)];
                    case 3:
                        status_1 = (_a.sent()).status;
                        if (Number(status_1) === 1) {
                            return [2 /*return*/, transaction_status_1.TX_STATUS.SUCCESS];
                        }
                        else {
                            return [2 /*return*/, transaction_status_1.TX_STATUS.FAILED];
                        }
                        _a.label = 4;
                    case 4: return [2 /*return*/, transaction_status_1.TX_STATUS.NOT_CONFIRMED];
                }
            });
        });
    };
    TransactionService.getFees = function (speed, chainProvider) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var fees, extractedFees;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, chainProvider.getFeeData()];
                    case 1:
                        fees = _a.sent();
                        extractedFees = {};
                        if (fees.maxFeePerGas) {
                            extractedFees.maxFeePerGas = this.calculateFee(fees.maxFeePerGas, (0, gas_price_multipliers_1.gasMultiplier)(speed));
                            extractedFees.maxPriorityFeePerGas = this.calculateFee(fees.maxPriorityFeePerGas, (0, gas_price_multipliers_1.gasMultiplier)(speed));
                        }
                        else {
                            extractedFees.gasPrice = this.calculateFee(fees.gasPrice, (0, gas_price_multipliers_1.gasMultiplier)(speed));
                        }
                        return [2 /*return*/, extractedFees];
                }
            });
        });
    };
    TransactionService.estimateGas = function (txRequest, chainProvider) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var estimation, error_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, chainProvider.estimateGas(txRequest)];
                    case 1:
                        estimation = _a.sent();
                        // do not add gas limit margin for sending native asset
                        if (estimation.eq(21000)) {
                            return [2 /*return*/, estimation];
                        }
                        // gas estimation is increased with 50%
                        else {
                            return [2 /*return*/, this.calculateGasMargin(estimation)];
                        }
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        return [2 /*return*/, this.calculateGasMargin(ethers_1.ethers.BigNumber.from("72000"))];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    TransactionService.toEthersBigNumber = function (a) {
        if (typeof a === "string")
            return ethers_1.BigNumber.from(a);
        return ethers_1.BigNumber.from(a.toFixed(0));
    };
    TransactionService.calculateGasMargin = function (value) {
        var offset = new bignumber_js_1["default"](value.toString())
            .multipliedBy(config_1.Config.GAS_LIMIT_MARGIN)
            .div("10000");
        var estimate = this.toEthersBigNumber(offset.plus(value.toString()).toFixed(0));
        return estimate;
    };
    TransactionService.calculateFee = function (base, multiplier) {
        return this.toEthersBigNumber(new bignumber_js_1["default"](base.toString()).times(multiplier));
    };
    TransactionService.transfer = function (sender, recipient, amount, chainId, pkOrProvider, isGasless) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var preparedTx;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, TransactionService.prepareTransaction({
                            from: sender,
                            to: recipient,
                            value: (0, utils_1.parseEther)(amount),
                            chainId: chainId
                        }, chainId)];
                    case 1:
                        preparedTx = _a.sent();
                        return [4 /*yield*/, (0, utils_2.getWallet)(pkOrProvider, chainId)];
                    case 2: return [4 /*yield*/, (_a.sent()).sendTransaction(preparedTx)];
                    case 3: return [2 /*return*/, (_a.sent()).hash];
                }
            });
        });
    };
    return TransactionService;
}());
exports.TransactionService = TransactionService;
